/* eslint-disable camelcase */
import Ajv from 'ajv-draft-04';
import { SelfDescribingJson, StructuredEvent } from '@snowplow/tracker-core';
import { InlineCompletionTriggerKind } from 'vscode-languageserver';
import { ConfigService, IConfig } from '../config_service';
import { LsFetch } from '../fetch';
import { log } from '../log';
import { AdditionalContext } from '../api_types';
import { shouldUseAdvancedContext } from '../advanced_context/helpers';
import { FeatureFlagService } from '../feature_flags';
import { ContextResolution } from '../advanced_context/context_resolvers/advanced_context_resolver';
import { IDocContext } from '../document_transformer_service';
import { getByteSize } from '../utils/byte_size';
import { GitLabApiClient } from '../api';
import { SUGGESTIONS_DEBOUNCE_INTERVAL_MS } from '../constants';
import { DefaultSupportedLanguagesService } from '../suggestion/supported_languages_service';
import { Snowplow } from './snowplow/snowplow';
import * as CodeSuggestionContextSchema from './schemas/code_suggestion_context-3-5-0.json';
import * as IdeExtensionContextSchema from './schemas/ide_extension_version-1-1-0.json';
import * as SnowplowMetaSchema from './schemas/snowplow_schema-1-0-0.json';
import { version as lsVersion } from './ls_info.json';
import {
  CODE_SUGGESTIONS_CATEGORY,
  DEFAULT_TRACKING_ENDPOINT,
  GC_TIME,
  SAAS_INSTANCE_URL,
  TELEMETRY_DISABLED_WARNING_MSG,
  TELEMETRY_ENABLED_MSG,
  TRACKING_EVENTS,
} from './constants';
import {
  GitlabRealm,
  IClientContext,
  ICodeSuggestionContextUpdate,
  ITelemetryOptions,
  SuggestionSource,
  TelemetryTracker,
} from './tracking_types';
import { canClientTrackEvent } from './utils';
import { CodeSuggestionTelemetryState } from './code_suggestions_telemetry_state_manager';

interface ContextItem {
  file_extension: string;
  type: AdditionalContext['type'];
  resolution_strategy: ContextResolution['strategy'];
  byte_size: number;
}

export interface ISnowplowCodeSuggestionContext {
  schema: string;
  data: {
    prefix_length?: number;
    suffix_length?: number;
    language?: string | null;
    gitlab_realm?: GitlabRealm;
    model_engine?: string | null;
    model_name?: string | null;
    api_status_code?: number | null;
    debounce_interval?: number | null;
    suggestion_source?: SuggestionSource;
    gitlab_global_user_id?: string | null;
    gitlab_instance_id?: string | null;
    gitlab_host_name?: string | null;
    gitlab_saas_duo_pro_namespace_ids: number[] | null;
    gitlab_instance_version: string | null;
    is_streaming?: boolean;
    is_invoked?: boolean | null;
    options_count?: number | null;
    accepted_option?: number | null;
    /**
     * boolean indicating whether the feature is enabled
     * and we sent context in the request
     */
    has_advanced_context?: boolean | null;
    /**
     * boolean indicating whether request is direct to cloud connector
     */
    is_direct_connection?: boolean | null;
    total_context_size_bytes?: number;
    content_above_cursor_size_bytes?: number;
    content_below_cursor_size_bytes?: number;
    /**
     * set of final context items sent to AI Gateway
     */
    context_items?: ContextItem[] | null;
    /**
     * total tokens used in request to model provider
     */
    input_tokens?: number | null;
    /**
     * total output tokens recieved from model provider
     */
    output_tokens?: number | null;
    /**
     * total tokens sent as context to AI Gateway
     */
    context_tokens_sent?: number | null;
    /**
     * total context tokens used in request to model provider
     */
    context_tokens_used?: number | null;
  };
}

interface ISnowplowClientContext {
  schema: string;
  data: {
    ide_name?: string | null;
    ide_vendor?: string | null;
    ide_version?: string | null;
    extension_name?: string | null;
    extension_version?: string | null;
    language_server_version?: string | null;
  };
}

const DEFAULT_SNOWPLOW_OPTIONS = {
  appId: 'gitlab_ide_extension',
  timeInterval: 5000,
  maxItems: 10,
};

export const EVENT_VALIDATION_ERROR_MSG = `Telemetry event context is not valid  - event won't be tracked.`;

export class SnowplowTracker implements TelemetryTracker {
  #snowplow: Snowplow;

  #ajv = new Ajv({ strict: false });

  #configService: ConfigService;

  #api: GitLabApiClient;

  #suggestionStateManager: CodeSuggestionTelemetryState = new CodeSuggestionTelemetryState(
    'Snowplow Telemetry',
  );

  #options: ITelemetryOptions = {
    enabled: true,
    baseUrl: SAAS_INSTANCE_URL,
    trackingUrl: DEFAULT_TRACKING_ENDPOINT,
    // the list of events that the client can track themselves
    actions: [],
  };

  #clientContext: ISnowplowClientContext = {
    schema: 'iglu:com.gitlab/ide_extension_version/jsonschema/1-1-0',
    data: {},
  };

  #lsFetch: LsFetch;

  #featureFlagService: FeatureFlagService;

  #gitlabRealm: GitlabRealm = GitlabRealm.saas;

  #codeSuggestionsContextMap = new Map<string, ISnowplowCodeSuggestionContext>();

  constructor(
    lsFetch: LsFetch,
    configService: ConfigService,
    featureFlagService: FeatureFlagService,
    api: GitLabApiClient,
  ) {
    this.#configService = configService;
    this.#configService.onConfigChange((config) => this.#reconfigure(config));
    this.#api = api;

    const trackingUrl = DEFAULT_TRACKING_ENDPOINT;

    this.#lsFetch = lsFetch;
    this.#configService = configService;
    this.#featureFlagService = featureFlagService;
    this.#snowplow = Snowplow.getInstance(this.#lsFetch, {
      ...DEFAULT_SNOWPLOW_OPTIONS,
      endpoint: trackingUrl,
      enabled: this.isEnabled.bind(this),
    });

    this.#options.trackingUrl = trackingUrl;
    this.#ajv.addMetaSchema(SnowplowMetaSchema);
  }

  isEnabled(): boolean {
    return Boolean(this.#options.enabled);
  }

  async #reconfigure(config: IConfig) {
    const { baseUrl } = config.client;
    const trackingUrl = config.client.telemetry?.trackingUrl;
    const enabled = config.client.telemetry?.enabled;
    const actions = config.client.telemetry?.actions;

    if (typeof enabled !== 'undefined') {
      this.#options.enabled = enabled;
      if (enabled === false) {
        log.warn(`Snowplow Telemetry: ${TELEMETRY_DISABLED_WARNING_MSG}`);
      } else if (enabled === true) {
        log.info(`Snowplow Telemetry: ${TELEMETRY_ENABLED_MSG}`);
      }
    }

    if (baseUrl) {
      this.#options.baseUrl = baseUrl;
      this.#gitlabRealm = baseUrl.endsWith(SAAS_INSTANCE_URL)
        ? GitlabRealm.saas
        : GitlabRealm.selfManaged;
    }

    if (trackingUrl && this.#options.trackingUrl !== trackingUrl) {
      await this.#snowplow.stop();
      this.#snowplow.destroy();
      this.#snowplow = Snowplow.getInstance(this.#lsFetch, {
        ...DEFAULT_SNOWPLOW_OPTIONS,
        endpoint: trackingUrl,
        enabled: this.isEnabled.bind(this),
      });
    }

    if (actions) {
      this.#options.actions = actions;
    }

    this.#setClientContext({
      extension: config.client.telemetry?.extension,
      ide: config.client.telemetry?.ide,
    });
  }

  #setClientContext(context: IClientContext) {
    this.#clientContext.data = {
      ide_name: context?.ide?.name ?? null,
      ide_vendor: context?.ide?.vendor ?? null,
      ide_version: context?.ide?.version ?? null,
      extension_name: context?.extension?.name ?? null,
      extension_version: context?.extension?.version ?? null,
      language_server_version: lsVersion ?? null,
    };
  }

  setCodeSuggestionsContext(
    uniqueTrackingId: string,
    context: Partial<ICodeSuggestionContextUpdate>,
  ) {
    const {
      documentContext,
      source = SuggestionSource.network,
      isStreaming,
      triggerKind,
      optionsCount,
      additionalContexts,
      isDirectConnection,
      model,
    } = context;

    const {
      gitlab_instance_id,
      gitlab_global_user_id,
      gitlab_host_name,
      gitlab_saas_duo_pro_namespace_ids,
    } = this.#configService.get('client.snowplowTrackerOptions') ?? {};

    if (source === SuggestionSource.cache) {
      log.debug(`Snowplow Telemetry: Retrieved suggestion from cache`);
    } else {
      log.debug(`Snowplow Telemetry: Received request to create a new suggestion`);
    }

    // Only auto-reject if client is set up to track accepted and not rejected events.
    if (
      canClientTrackEvent(this.#options.actions, TRACKING_EVENTS.ACCEPTED) &&
      !canClientTrackEvent(this.#options.actions, TRACKING_EVENTS.REJECTED)
    ) {
      this.rejectOpenedSuggestions();
    }

    setTimeout(() => {
      if (this.#codeSuggestionsContextMap.has(uniqueTrackingId)) {
        this.#codeSuggestionsContextMap.delete(uniqueTrackingId);
        this.#suggestionStateManager.deleteSuggestion(uniqueTrackingId);
      }
    }, GC_TIME);

    // Use the language provided by the model, if present.
    // Otherwise determine the language from the file extension.
    const language =
      model?.lang ??
      DefaultSupportedLanguagesService.getLanguageForFile(documentContext?.fileRelativePath) ??
      null;

    const advancedContextData = this.#getAdvancedContextData({
      additionalContexts,
      documentContext,
    });
    this.#codeSuggestionsContextMap.set(uniqueTrackingId, {
      schema: 'iglu:com.gitlab/code_suggestions_context/jsonschema/3-5-0',
      data: {
        suffix_length: documentContext?.suffix.length ?? 0,
        prefix_length: documentContext?.prefix.length ?? 0,
        gitlab_realm: this.#gitlabRealm,
        model_engine: model?.engine ?? null,
        model_name: model?.name ?? null,
        language,
        api_status_code: null,
        debounce_interval: source === SuggestionSource.cache ? 0 : SUGGESTIONS_DEBOUNCE_INTERVAL_MS,
        suggestion_source: source,
        gitlab_global_user_id: gitlab_global_user_id ?? null,
        gitlab_host_name: gitlab_host_name ?? null,
        gitlab_instance_id: gitlab_instance_id ?? null,
        gitlab_saas_duo_pro_namespace_ids: gitlab_saas_duo_pro_namespace_ids ?? null,
        gitlab_instance_version: this.#api.instanceVersion ?? null,
        is_streaming: isStreaming ?? false,
        is_invoked: SnowplowTracker.#isCompletionInvoked(triggerKind),
        options_count: optionsCount ?? null,
        has_advanced_context: advancedContextData.hasAdvancedContext,
        is_direct_connection: isDirectConnection ?? null,
        total_context_size_bytes: advancedContextData.totalContextSizeBytes,
        content_above_cursor_size_bytes: advancedContextData.contentAboveCursorSizeBytes,
        content_below_cursor_size_bytes: advancedContextData.contentBelowCursorSizeBytes,
        context_items: advancedContextData.contextItems,
      },
    });

    this.updateSuggestionState(uniqueTrackingId, TRACKING_EVENTS.REQUESTED);
  }

  // FIXME: the set and update context methods have similar logic and they should have to grow linearly with each new attribute
  // the solution might be some generic update method used by both
  updateCodeSuggestionsContext(
    uniqueTrackingId: string,
    contextUpdate: Partial<ICodeSuggestionContextUpdate>,
  ) {
    const context = this.#codeSuggestionsContextMap.get(uniqueTrackingId);
    const { model, status, optionsCount, acceptedOption, isDirectConnection } = contextUpdate;

    if (context) {
      if (model) {
        if (model.lang) {
          context.data.language = model.lang;
        }
        context.data.model_engine = model.engine ?? null;
        context.data.model_name = model.name ?? null;
        context.data.input_tokens = model?.tokens_consumption_metadata?.input_tokens ?? null;
        context.data.output_tokens = model.tokens_consumption_metadata?.output_tokens ?? null;
        context.data.context_tokens_sent =
          model.tokens_consumption_metadata?.context_tokens_sent ?? null;
        context.data.context_tokens_used =
          model.tokens_consumption_metadata?.context_tokens_used ?? null;
      }

      if (status) {
        context.data.api_status_code = status;
      }

      if (optionsCount) {
        context.data.options_count = optionsCount;
      }

      if (isDirectConnection !== undefined) {
        context.data.is_direct_connection = isDirectConnection;
      }

      if (acceptedOption) {
        context.data.accepted_option = acceptedOption;
      }

      this.#codeSuggestionsContextMap.set(uniqueTrackingId, context);
    }
  }

  async #trackCodeSuggestionsEvent(eventType: TRACKING_EVENTS, uniqueTrackingId: string) {
    if (!this.isEnabled()) {
      return;
    }

    const event: StructuredEvent = {
      category: CODE_SUGGESTIONS_CATEGORY,
      action: eventType,
      label: uniqueTrackingId,
    };

    try {
      const contexts: SelfDescribingJson[] = [this.#clientContext];
      const codeSuggestionContext = this.#codeSuggestionsContextMap.get(uniqueTrackingId);

      if (codeSuggestionContext) {
        contexts.push(codeSuggestionContext);
      }

      const suggestionContextValid = this.#ajv.validate(
        CodeSuggestionContextSchema,
        codeSuggestionContext?.data,
      );
      if (!suggestionContextValid) {
        log.warn(EVENT_VALIDATION_ERROR_MSG);
        log.debug(JSON.stringify(this.#ajv.errors, null, 2));
        return;
      }
      const ideExtensionContextValid = this.#ajv.validate(
        IdeExtensionContextSchema,
        this.#clientContext?.data,
      );
      if (!ideExtensionContextValid) {
        log.warn(EVENT_VALIDATION_ERROR_MSG);
        log.debug(JSON.stringify(this.#ajv.errors, null, 2));
        return;
      }

      await this.#snowplow.trackStructEvent(event, contexts);
    } catch (error) {
      log.warn(`Snowplow Telemetry: Failed to track telemetry event: ${eventType}`, error);
    }
  }

  updateSuggestionState(uniqueTrackingId: string, newState: TRACKING_EVENTS): void {
    const isStreaming = Boolean(
      this.#codeSuggestionsContextMap.get(uniqueTrackingId)?.data.is_streaming,
    );
    if (this.#suggestionStateManager.canUpdateState(uniqueTrackingId, newState, isStreaming)) {
      this.#suggestionStateManager.updateSuggestionState(uniqueTrackingId, newState, isStreaming);
      this.#trackCodeSuggestionsEvent(newState, uniqueTrackingId).catch((e) =>
        log.warn('Snowplow Telemetry: Could not track telemetry', e),
      );
    }
  }

  rejectOpenedSuggestions() {
    log.debug(`Snowplow Telemetry: Reject all opened suggestions`);
    this.#suggestionStateManager
      .getOpenedSuggestions()
      .forEach((id) => this.updateSuggestionState(id, TRACKING_EVENTS.REJECTED));
  }

  #hasAdvancedContext(advancedContexts?: AdditionalContext[]): boolean | null {
    const advancedContextFeatureFlagsEnabled = shouldUseAdvancedContext(
      this.#featureFlagService,
      this.#configService,
    );

    if (advancedContextFeatureFlagsEnabled) {
      return Boolean(advancedContexts?.length);
    }

    return null;
  }

  #getAdvancedContextData({
    additionalContexts,
    documentContext,
  }: {
    additionalContexts?: AdditionalContext[];
    documentContext?: IDocContext;
  }) {
    const hasAdvancedContext = this.#hasAdvancedContext(additionalContexts);

    const contentAboveCursorSizeBytes = documentContext?.prefix
      ? getByteSize(documentContext.prefix)
      : 0;
    const contentBelowCursorSizeBytes = documentContext?.suffix
      ? getByteSize(documentContext.suffix)
      : 0;

    const contextItems: ContextItem[] | null =
      additionalContexts?.map((item) => ({
        file_extension: item.name.split('.').pop() || '',
        type: item.type,
        resolution_strategy: item.resolution_strategy,
        byte_size: item?.content ? getByteSize(item.content) : 0,
      })) ?? null;

    const totalContextSizeBytes =
      contextItems?.reduce((total, item) => total + item.byte_size, 0) ?? 0;

    return {
      totalContextSizeBytes,
      contentAboveCursorSizeBytes,
      contentBelowCursorSizeBytes,
      contextItems,
      hasAdvancedContext,
    };
  }

  static #isCompletionInvoked(triggerKind?: InlineCompletionTriggerKind): boolean | null {
    let isInvoked = null;

    if (triggerKind === InlineCompletionTriggerKind.Invoked) {
      isInvoked = true;
    } else if (triggerKind === InlineCompletionTriggerKind.Automatic) {
      isInvoked = false;
    }

    return isInvoked;
  }
}
